import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const TermsAndConditions = () => {
  const navigate = useNavigate();

  const handleAccept = () => {
    // Set flag in localStorage so the registration form can detect acceptance
    localStorage.setItem('termsAccepted', 'true');
    // If opened in a new tab from registration, close this tab
    if (window.opener) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border px-4 sm:px-6 lg:px-8 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Scale" size={20} color="white" />
              </div>
              <span className="text-xl font-heading font-bold text-primary">LawDesk KE</span>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-text-secondary hover:text-text-primary transition-smooth"
            >
              <Icon name="ArrowLeft" size={18} />
              <span className="text-sm">Back</span>
            </button>
          </div>
        </div>
      </header>
      {/* Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-surface rounded-xl shadow-card border border-border p-8 md:p-12">
            {/* Title */}
            <div className="mb-10">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-heading font-bold text-text-primary">Terms &amp; Conditions</h1>
                  <p className="text-text-secondary text-sm">Last updated: March 2026</p>
                </div>
              </div>
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <p className="text-sm text-primary">
                  Please read these Terms and Conditions carefully before using the LawDesk KE platform. By registering or using our services, you agree to be bound by these terms.
                </p>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-8 text-text-secondary">

              <section>
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  <span>Acceptance of Terms</span>
                </h2>
                <p className="text-sm leading-relaxed">
                  By accessing or using LawDesk KE (&quot;the Platform&quot;), you agree to be bound by these Terms and Conditions and all applicable laws and regulations of Kenya. If you do not agree with any of these terms, you are prohibited from using or accessing this platform.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  <span>Description of Services</span>
                </h2>
                <p className="text-sm leading-relaxed mb-3">
                  LawDesk KE is a legal technology platform that connects clients with qualified, licensed lawyers in Kenya. Our services include:
                </p>
                <ul className="text-sm space-y-2 ml-4">
                  <li className="flex items-start space-x-2"><Icon name="CheckCircle" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Lawyer matching and discovery based on legal needs</span></li>
                  <li className="flex items-start space-x-2"><Icon name="CheckCircle" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Secure communication between clients and lawyers</span></li>
                  <li className="flex items-start space-x-2"><Icon name="CheckCircle" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Case management and document sharing tools</span></li>
                  <li className="flex items-start space-x-2"><Icon name="CheckCircle" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Video consultation scheduling and hosting</span></li>
                  <li className="flex items-start space-x-2"><Icon name="CheckCircle" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Invoice and payment management</span></li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  <span>User Accounts &amp; Registration</span>
                </h2>
                <p className="text-sm leading-relaxed mb-3">
                  To use certain features of the Platform, you must register for an account. You agree to:
                </p>
                <ul className="text-sm space-y-2 ml-4">
                  <li className="flex items-start space-x-2"><Icon name="Dot" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Provide accurate, current, and complete information during registration</span></li>
                  <li className="flex items-start space-x-2"><Icon name="Dot" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Maintain and promptly update your account information</span></li>
                  <li className="flex items-start space-x-2"><Icon name="Dot" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Keep your password confidential and not share it with third parties</span></li>
                  <li className="flex items-start space-x-2"><Icon name="Dot" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Notify us immediately of any unauthorized use of your account</span></li>
                  <li className="flex items-start space-x-2"><Icon name="Dot" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Be at least 18 years of age to create an account</span></li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                  <span>Lawyer Verification &amp; Conduct</span>
                </h2>
                <p className="text-sm leading-relaxed mb-3">
                  All lawyers on the LawDesk KE platform are required to:
                </p>
                <ul className="text-sm space-y-2 ml-4">
                  <li className="flex items-start space-x-2"><Icon name="CheckCircle" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Hold a valid practicing certificate from the Law Society of Kenya (LSK)</span></li>
                  <li className="flex items-start space-x-2"><Icon name="CheckCircle" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Comply with the Advocates Act (Cap 16) and LSK Code of Conduct</span></li>
                  <li className="flex items-start space-x-2"><Icon name="CheckCircle" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Maintain client confidentiality and attorney-client privilege</span></li>
                  <li className="flex items-start space-x-2"><Icon name="CheckCircle" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Provide accurate information about their qualifications and experience</span></li>
                </ul>
                <p className="text-sm leading-relaxed mt-3">
                  LawDesk KE is a platform facilitating connections and does not provide legal advice directly. The legal advice and services are provided solely by the individual lawyers.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
                  <span>Privacy &amp; Data Protection</span>
                </h2>
                <p className="text-sm leading-relaxed mb-3">
                  We are committed to protecting your personal data in accordance with the Kenya Data Protection Act, 2019. We collect and process personal data to:
                </p>
                <ul className="text-sm space-y-2 ml-4">
                  <li className="flex items-start space-x-2"><Icon name="Dot" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Provide and improve our services</span></li>
                  <li className="flex items-start space-x-2"><Icon name="Dot" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Match clients with appropriate lawyers</span></li>
                  <li className="flex items-start space-x-2"><Icon name="Dot" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Send service-related communications</span></li>
                  <li className="flex items-start space-x-2"><Icon name="Dot" size={14} className="text-primary mt-0.5 flex-shrink-0" /><span>Comply with legal obligations</span></li>
                </ul>
                <p className="text-sm leading-relaxed mt-3">
                  We do not sell your personal data to third parties. You have the right to access, correct, or delete your personal data at any time by contacting us at <strong>support@lawdesk.co.ke</strong>.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">6</span>
                  <span>Fees &amp; Payments</span>
                </h2>
                <p className="text-sm leading-relaxed">
                  LawDesk KE charges a platform service fee for facilitating connections between clients and lawyers. Lawyer consultation fees are set independently by each lawyer and are clearly displayed on their profiles. All fees are in Kenyan Shillings (KES) unless otherwise stated. Payments are processed securely through our payment partners.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">7</span>
                  <span>Limitation of Liability</span>
                </h2>
                <p className="text-sm leading-relaxed">
                  LawDesk KE acts solely as an intermediary platform. We are not responsible for the quality, accuracy, or outcomes of legal services provided by lawyers on our platform. Our liability is limited to the platform service fees paid in the 3 months preceding any claim. We are not liable for any indirect, incidental, or consequential damages.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">8</span>
                  <span>Prohibited Conduct</span>
                </h2>
                <p className="text-sm leading-relaxed mb-3">Users are prohibited from:</p>
                <ul className="text-sm space-y-2 ml-4">
                  <li className="flex items-start space-x-2"><Icon name="XCircle" size={14} className="text-error mt-0.5 flex-shrink-0" /><span>Providing false or misleading information</span></li>
                  <li className="flex items-start space-x-2"><Icon name="XCircle" size={14} className="text-error mt-0.5 flex-shrink-0" /><span>Impersonating any person or entity</span></li>
                  <li className="flex items-start space-x-2"><Icon name="XCircle" size={14} className="text-error mt-0.5 flex-shrink-0" /><span>Using the platform for any unlawful purpose</span></li>
                  <li className="flex items-start space-x-2"><Icon name="XCircle" size={14} className="text-error mt-0.5 flex-shrink-0" /><span>Harassing, threatening, or abusing other users</span></li>
                  <li className="flex items-start space-x-2"><Icon name="XCircle" size={14} className="text-error mt-0.5 flex-shrink-0" /><span>Attempting to gain unauthorized access to the platform</span></li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">9</span>
                  <span>Governing Law</span>
                </h2>
                <p className="text-sm leading-relaxed">
                  These Terms and Conditions are governed by and construed in accordance with the laws of Kenya. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of Kenya.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">10</span>
                  <span>Contact Us</span>
                </h2>
                <p className="text-sm leading-relaxed mb-3">
                  If you have any questions about these Terms and Conditions, please contact us:
                </p>
                <div className="bg-secondary-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <Icon name="Mail" size={14} className="text-primary" />
                    <span><strong>Email:</strong> support@lawdesk.co.ke</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Icon name="Phone" size={14} className="text-primary" />
                    <span><strong>Phone:</strong> 0727265443</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Icon name="Globe" size={14} className="text-primary" />
                    <span><strong>Website:</strong> lawdesk.co.ke</span>
                  </div>
                </div>
              </section>
            </div>

            {/* Footer */}
            <div className="mt-10 pt-6 border-t border-border text-center">
              <p className="text-xs text-text-secondary">
                &copy; {new Date()?.getFullYear()} LawDesk KE. All rights reserved. These terms are effective as of March 2026.
              </p>
              <button
                onClick={handleAccept}
                className="mt-4 bg-primary text-white px-8 py-3 rounded-lg font-medium hover:bg-primary-700 transition-smooth"
              >
                I Understand &amp; Accept
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsAndConditions;